import type { DyspatchEvent, WorkerRecord } from '@dyspatch/core';
import type { PersonRow } from './api';
import { byNumber, byRank, byText, byTime, type SortOption } from './sorting';

/**
 * The key lists for the three admin pages the sort primitive does not draw a
 * table for on its own (issue #74): `/admin/people`, `/agents`, `/admin/audit`.
 *
 * This lives in `lib/` rather than beside each route, unlike `/jobs`'s
 * `jobSortOptions`, for one reason: a route file pulls in `@/components/*`
 * through the `@/` alias, which `packages/hub/test` cannot resolve, so a route
 * module cannot be imported from a test at all. This file only imports types
 * (erased at compile time) and `./sorting`, so `packages/hub/test/admin-sort.
 * test.ts` can reach it the same way `sorting.test.ts` reaches `../web/src/lib/
 * sorting` -- by relative path, never the alias.
 */

/**
 * What a member's role is, as the word `byRank` orders on.
 *
 * The rank list is already "the interesting end first": an admin is the
 * exception on this page, so opening the role column ascending opens it on
 * admins, and it needs no `natural` override to do that.
 */
export function roleOf(person: PersonRow): 'admin' | 'member' {
  return person.isAdmin ? 'admin' : 'member';
}

/**
 * What the members table can be ordered by.
 *
 * The issue that asked for this table called the usage key "spend"; this table
 * has no money or token figure, only `account.load` (a concurrent-task count)
 * and `account.headroom` (percent of the Claude window left), so the keys are
 * named for what they are rather than for what the issue guessed they were.
 *
 * `inUse` is `account.load`: a person with no device online has drawn nothing
 * from their window, which is a real zero, so it sorts to the quiet end under
 * the default (busiest first) without any special-casing.
 *
 * `headroom` opens least-headroom-first (ascending, the plain default), since
 * that is the row worth looking at first: who is closest to running out. A
 * person with no device online has no headroom figure to report at all -- it
 * is not that they have none left, it is that nothing is drawing on the
 * window -- so they are given the same reading as full headroom, which sorts
 * them to the far end rather than falsely reading as the most urgent row.
 */
export function peopleSortOptions(): SortOption<PersonRow>[] {
  return [
    { value: 'name', label: 'Name', compare: byText((p) => p.name || p.email) },
    {
      value: 'inUse',
      label: 'In use',
      compare: byNumber((p) => p.account?.load ?? 0),
      natural: 'desc',
    },
    {
      value: 'headroom',
      label: 'Headroom',
      compare: byNumber((p) => p.account?.headroom ?? 1),
    },
    {
      value: 'devices',
      label: 'Devices',
      compare: byNumber((p) => p.devices.length),
      natural: 'desc',
    },
    { value: 'role', label: 'Role', compare: byRank(['admin', 'member'], roleOf) },
    {
      value: 'seen',
      label: 'Last seen',
      compare: byTime((p) => p.lastSeenAt),
      natural: 'desc',
    },
  ];
}

/**
 * What the fleet table can be ordered by.
 *
 * "Tasks run" counts a device's tasks in the list the page already has
 * (`useTasks`), not a lifetime total the hub keeps nowhere: like the jobs
 * page's progress column, it is capped at whatever `/api/tasks` returns, which
 * is the same cap the rest of that page already lives with.
 *
 * "Last heartbeat" opens most-recent-first, the same convention `updated` uses
 * on `/jobs`: a device that has never reported sorts to the quiet end rather
 * than the busy one. Reversing it is how you ask "which has been quiet
 * longest", the question issue #74 raised for this page.
 */
export function agentSortOptions(
  tasksRunOf: (worker: WorkerRecord) => number,
): SortOption<WorkerRecord>[] {
  return [
    { value: 'name', label: 'Name', compare: byText((w) => w.name) },
    {
      value: 'heartbeat',
      label: 'Last heartbeat',
      compare: byTime((w) => w.lastHeartbeatAt),
      natural: 'desc',
    },
    {
      value: 'tasks',
      label: 'Tasks run',
      compare: byNumber((w) => tasksRunOf(w)),
      natural: 'desc',
    },
  ];
}

/**
 * The audit log's family filter, shared between the chips (`admin-audit.tsx`)
 * and the `family` sort key below, so the two cannot drift into disagreeing
 * about what a family is.
 */
export const FAMILIES = [
  { key: 'all', label: 'Everything', match: () => true },
  { key: 'brief', label: 'Jobs', match: (kind: string) => kind.startsWith('brief.') },
  { key: 'task', label: 'Tasks', match: (kind: string) => kind.startsWith('task.') },
  { key: 'worker', label: 'Agents', match: (kind: string) => kind.startsWith('worker.') },
  { key: 'person', label: 'People', match: (kind: string) => kind.startsWith('person.') },
] as const;

export type FamilyKey = (typeof FAMILIES)[number]['key'];

/** The family chip an event belongs to, for the `family` sort key below. `all`
 * matches everything, so it is left out: an event either has a real family or
 * it has none, and `byRank` already sends "none" to the end. */
export const FAMILY_RANK = FAMILIES.filter((entry) => entry.key !== 'all').map(
  (entry) => entry.key,
);

export function familyOf(kind: string): string {
  return FAMILIES.find((entry) => entry.key !== 'all' && entry.match(kind))?.key ?? '';
}

/**
 * What the journal can be ordered by.
 *
 * `time` compares on `seq`, not `createdAt`: `seq` is the append order the hub
 * assigns and cannot tie, where two events written in the same millisecond
 * can. This is also why the default order does not change from what the page
 * had before this existed (`.sort((a, b) => b.seq - a.seq)`) -- `seq`
 * descending and time descending are the same order, but only one of them is
 * safe to sort on without the primitive's id tiebreak doing the work.
 */
export function auditSortOptions(): SortOption<DyspatchEvent>[] {
  return [
    { value: 'time', label: 'Time', compare: byNumber((event) => event.seq), natural: 'desc' },
    {
      value: 'family',
      label: 'Family',
      compare: byRank(FAMILY_RANK, (event) => familyOf(event.kind)),
    },
    { value: 'actor', label: 'Actor', compare: byText((event) => event.actor) },
  ];
}
